import { useCallback, useEffect, useMemo, useState } from "react";
import { UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  message,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Typography,
  Upload,
} from "antd";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import AppContent from "common/AppContent";
import AppPageHeader from "common/PageHeader";
import CustomDatePicker from "common/CustomDatePicker";
import { deleteData, getData, postData, putData } from "apiServices/common";
import {
  MER_SAMPLE_REQUISITION_ADD,
  MER_SAMPLE_REQUISITION_BUYER_DATA,
  MER_SAMPLE_REQUISITION_DELETE_IMAGE,
  MER_SAMPLE_REQUISITION_NEXT_REFERENCE,
  MER_SAMPLE_REQUISITION_SAMPLE_TYPES,
  MER_SAMPLE_REQUISITION_SHOW,
  MER_SAMPLE_REQUISITION_STYLES,
  MER_SAMPLE_REQUISITION_UPDATE,
  MER_SAMPLE_REQUISITION_UPLOAD_IMAGE,
} from "apiServices/API_ENDPOINTS";
import { SAMPLE_REQUISITION_EDIT_PERMISSION } from "routes/permissions";
import { hasPermission } from "utils/functions";

const { Title, Text } = Typography;
const { TextArea } = Input;

const GUTTER = [16, 0];
const QUARTER = { xs: 24, sm: 12, lg: 6 };
const FOURTH = { xs: 24, md: 6 };
const HALF = { xs: 24, md: 12 };

const LIST_PATH = "/merchandising/sample-requisition";
const DATE_PAYLOAD_FORMAT = "YYYY-MM-DD";
const API_BASE_URL = process.env.REACT_APP_BASE_URL;

const DATE_FIELDS = [
  "requisition_date",
  "gmt_ex_country_date",
  "sample_requirement_date",
  "receive_date",
  "expected_submission_date",
  "actual_submission_date",
];

const TEXT_FIELDS = [
  "style_description",
  "model",
  "item",
  "color",
  "size",
  "pattern_ref",
  "fabric_construction",
  "pocketing",
  "interlining",
  "wash_detail",
  "threads",
  "section",
  "special_instruction",
  "body_fabric_swatch",
  "contrast_fabric_swatch",
];

const YES_NO_FIELDS = [
  { label: "Button", name: "has_button" },
  { label: "Rivet", name: "has_rivet" },
  { label: "Zipper", name: "has_zipper" },
  { label: "Printing", name: "has_printing" },
  { label: "Embroidery", name: "has_embroidery" },
  { label: "Contrast Fabric", name: "has_contrast_fabric" },
  { label: "Contrast Thread", name: "has_contrast_thread" },
];

const BOOLEAN_FIELDS = YES_NO_FIELDS.map((f) => f.name);

const YES_NO_OPTIONS = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

const CHECKLIST_ITEMS = [
  "SAMPLE",
  "TECH PACK",
  "COMMENTS",
  "TRIMS CARD",
  "FABRIC SHRINKAGE",
  "FABRIC-01",
  "FABRIC-02",
  "LINING",
  "THREAD-01",
  "THREAD-02",
  "THREAD-03",
  "SIZE LABEL",
  "MAIN LABEL",
  "CARE LABEL",
  "FIT LABEL",
  "BUTTON-01",
  "BUTTON-02",
  "ZIPPER",
  "TAPE",
  "ELASTIC",
  "EMB/PRINT",
  "WASH/NON WASH",
  "HAND TAG",
  "UPC STICKER",
  "POLY BAG",
];

const ALLOWED_FILE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "pdf", "xls", "xlsx", "doc", "docx"];

const defaultChecklist = () =>
  CHECKLIST_ITEMS.map((item_name) => ({
    item_name,
    is_received: false,
    received_date: null,
    remarks: "",
  }));

const subStylesOf = (style) => style?.subStyle ?? style?.sub_style ?? [];

const toImageUrl = (path) => (path ? `${API_BASE_URL}/${String(path).replace(/^\/+/, "")}` : undefined);

const toUploadFile = (image) => ({
  uid: `saved-${image.id}`,
  name: image.original_name || image.image_name,
  status: "done",
  url: toImageUrl(image.image_path),
  imageId: image.id,
  isNew: false,
});

const buildStyleDescription = (styles) =>
  styles
    .filter((style) => style?.description)
    .map((style) => (styles.length > 1 ? `${style.style_no}: ${style.description}` : style.description))
    .join("\n");

const mergeById = (current, incoming) => {
  const ids = new Set(current.map((item) => item.id));
  return [...current, ...incoming.filter((item) => item && !ids.has(item.id))];
};

const YesNoRadio = (props) => <Radio.Group options={YES_NO_OPTIONS} optionType="button" size="small" {...props} />;

const Section = ({ title, children, stretch = false }) => (
  <Card
    size="small"
    title={title}
    style={{ marginBottom: 16, width: "100%", ...(stretch ? { flex: "1 1 auto" } : {}) }}
    headStyle={{ background: "#fafafa", fontWeight: 600 }}
    bodyStyle={{ paddingBottom: stretch ? 12 : 0 }}
  >
    {children}
  </Card>
);

export default function SampleRequisitionForm({ mode = "create" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const isView = mode === "view";
  const isEdit = mode === "edit";

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [referenceNo, setReferenceNo] = useState(null);
  const [buyers, setBuyers] = useState([]);
  const [styles, setStyles] = useState([]);
  const [stylesLoading, setStylesLoading] = useState(false);
  const [sampleTypes, setSampleTypes] = useState([]);
  const [imageList, setImageList] = useState([]);

  const buyerId = Form.useWatch("buyer_id", form);
  const styleIds = Form.useWatch("style_ids", form) ?? [];
  const subStyleOptions = useMemo(() => {
    const selected = styles.filter((style) => styleIds.includes(style.id));
    const showStyleNo = selected.length > 1;
    return selected.flatMap((style) =>
      subStylesOf(style).map((subStyle) => ({
        label: showStyleNo ? `${style.style_no} - ${subStyle.sub_style_no}` : subStyle.sub_style_no,
        value: subStyle.id,
      })),
    );
  }, [styles, styleIds]);
  const buyingOffices = useMemo(
    () => buyers.find((buyer) => buyer.id === buyerId)?.buying_offices ?? [],
    [buyers, buyerId],
  );

  const loadStyles = useCallback(async (selectedBuyerId, buyingOfficeId, keepStyles = []) => {
    if (!selectedBuyerId) {
      setStyles([]);
      return [];
    }
    setStylesLoading(true);
    const response = await getData(MER_SAMPLE_REQUISITION_STYLES, false, {
      buyer_id: selectedBuyerId,
      ...(buyingOfficeId && { buying_office_id: buyingOfficeId }),
    });
    setStylesLoading(false);

    const loaded = response?.data?.code === 200 ? response.data.data ?? [] : [];
    const loadedIds = new Set(loaded.map((style) => style.id));
    const merged = [...loaded, ...keepStyles.filter((style) => !loadedIds.has(style.id))];
    setStyles(merged);
    return merged;
  }, []);

  useEffect(() => {
    const loadDropdowns = async () => {
      const [buyerResponse, sampleTypeResponse] = await Promise.all([
        getData(MER_SAMPLE_REQUISITION_BUYER_DATA),
        getData(MER_SAMPLE_REQUISITION_SAMPLE_TYPES),
      ]);
      if (buyerResponse?.data?.code === 200) setBuyers((prev) => mergeById(buyerResponse.data.data ?? [], prev));
      if (sampleTypeResponse?.data?.code === 200) {
        setSampleTypes((prev) => mergeById(sampleTypeResponse.data.data ?? [], prev));
      }
    };
    loadDropdowns();
  }, []);

  useEffect(() => {
    if (mode !== "create") return;

    const loadNextReference = async () => {
      const response = await getData(MER_SAMPLE_REQUISITION_NEXT_REFERENCE);
      if (response?.data?.code === 200) setReferenceNo(response.data.data?.reference_no ?? null);
    };
    loadNextReference();
  }, [mode]);

  useEffect(() => {
    if (!id || mode === "create") return;

    const loadRecord = async () => {
      setLoading(true);
      const response = await getData(MER_SAMPLE_REQUISITION_SHOW(id));
      setLoading(false);
      if (response?.data?.code !== 200) return;

      const record = response.data.data;
      setReferenceNo(record.reference_no);
      setImageList((record.images ?? []).map(toUploadFile));

      setSampleTypes((prev) => mergeById(prev, [record.sample_type]));
      if (record.buyer) {
        setBuyers((prev) =>
          mergeById(prev, [{ ...record.buyer, buying_offices: record.buying_office ? [record.buying_office] : [] }]),
        );
      }
      await loadStyles(record.buyer_id, record.buying_office_id, record.styles ?? []);

      form.setFieldsValue({
        ...Object.fromEntries(DATE_FIELDS.map((field) => [field, record[field] ? moment(record[field]) : null])),
        ...Object.fromEntries(TEXT_FIELDS.map((field) => [field, record[field] ?? ""])),
        ...Object.fromEntries(BOOLEAN_FIELDS.map((field) => [field, !!record[field]])),
        buyer_id: record.buyer_id,
        buying_office_id: record.buying_office_id,
        style_ids: (record.styles ?? []).map((style) => style.id),
        sub_style_ids: (record.sub_styles ?? []).map((subStyle) => subStyle.id),
        sample_type_id: record.sample_type_id,
        sample_qty: record.sample_qty,
        checklist: (record.checklist_items ?? []).length
          ? [...record.checklist_items]
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((item) => ({
                item_name: item.item_name,
                is_received: !!item.is_received,
                received_date: item.received_date ? moment(item.received_date) : null,
                remarks: item.remarks ?? "",
              }))
          : defaultChecklist(),
      });
    };
    loadRecord();
  }, [id, mode, form, loadStyles]);

  const onBuyerChange = (value) => {
    const offices = buyers.find((buyer) => buyer.id === value)?.buying_offices ?? [];
    const autoOffice = offices.length === 1 ? offices[0].id : undefined;
    form.setFieldsValue({ buying_office_id: autoOffice, style_ids: [], sub_style_ids: [], style_description: "" });
    loadStyles(value, autoOffice);
  };

  const onBuyingOfficeChange = async (value) => {
    const loaded = await loadStyles(form.getFieldValue("buyer_id"), value);
    const keptStyles = loaded.filter((style) => (form.getFieldValue("style_ids") ?? []).includes(style.id));
    const keptStyleIds = keptStyles.map((style) => style.id);
    const allowedSubIds = new Set(keptStyles.flatMap((style) => subStylesOf(style).map((subStyle) => subStyle.id)));
    form.setFieldsValue({
      style_ids: keptStyleIds,
      sub_style_ids: (form.getFieldValue("sub_style_ids") ?? []).filter((subStyleId) => allowedSubIds.has(subStyleId)),
      style_description: buildStyleDescription(keptStyles),
    });
  };

  const onStylesChange = (nextStyleIds) => {
    const selected = nextStyleIds.map((styleId) => styles.find((style) => style.id === styleId)).filter(Boolean);
    const allowedSubIds = new Set(selected.flatMap((style) => subStylesOf(style).map((subStyle) => subStyle.id)));
    form.setFieldsValue({
      style_description: buildStyleDescription(selected),
      sub_style_ids: (form.getFieldValue("sub_style_ids") ?? []).filter((subStyleId) => allowedSubIds.has(subStyleId)),
    });
  };

  const beforeAttachmentUpload = (file) => {
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!ALLOWED_FILE_EXTENSIONS.includes(extension)) {
      message.error("Only image, PDF, Excel, or Word files are allowed");
      return Upload.LIST_IGNORE;
    }
    if (file.size > 10 * 1024 * 1024) {
      message.error("File size cannot be greater than 10MB");
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const onImageChange = ({ file, fileList }) => {
    setImageList(
      fileList
        .map((item) => {
          if (item.uid !== file.uid || file.status !== "done") return item;
          if (file.response?.code !== 200) {
            message.error(file.response?.message?.[0] || `${file.name} upload failed`);
            return null;
          }
          return {
            ...item,
            name: file.response.data.original_name || item.name,
            imageId: file.response.data.id,
            url: toImageUrl(file.response.data.image_path),
            isNew: true,
          };
        })
        .filter(Boolean),
    );
    if (file.status === "error") message.error(`${file.name} upload failed`);
  };

  const onImageRemove = (file) => {
    if (file.isNew && file.imageId) deleteData(MER_SAMPLE_REQUISITION_DELETE_IMAGE(file.imageId));
    return true;
  };

  const onFinish = async (values) => {
    if (imageList.some((file) => file.status === "uploading")) {
      message.warning("Please wait for the files to finish uploading");
      return;
    }

    const { checklist, ...rest } = values;
    const payload = {
      ...rest,
      ...Object.fromEntries(
        DATE_FIELDS.map((field) => [field, values[field] ? moment(values[field]).format(DATE_PAYLOAD_FORMAT) : null]),
      ),
      ...Object.fromEntries(BOOLEAN_FIELDS.map((field) => [field, !!values[field]])),
      sub_style_ids: values.sub_style_ids ?? [],
      image_ids: imageList.map((file) => file.imageId).filter(Boolean),
      checklist: (checklist ?? []).map((item) => ({
        item_name: item.item_name,
        is_received: !!item.is_received,
        received_date: item.received_date ? moment(item.received_date).format(DATE_PAYLOAD_FORMAT) : null,
        remarks: item.remarks || null,
      })),
    };

    setSaving(true);
    const response = isEdit
      ? await putData(MER_SAMPLE_REQUISITION_UPDATE(id), payload)
      : await postData(MER_SAMPLE_REQUISITION_ADD, payload);
    setSaving(false);

    if (response?.code === 200) {
      const savedReference = response.data?.reference_no;
      if (!isEdit && savedReference && referenceNo && savedReference !== referenceNo) {
        message.info(`${referenceNo} was taken by another user, saved as ${savedReference}`);
      }
      message.success(`Sample Requisition ${isEdit ? "updated" : "created"} successfully`);
      navigate(`${LIST_PATH}/view/${response.data.id}`);
    }
  };

  const pageTitle = { create: "Add Sample Requisition", edit: "Edit Sample Requisition", view: "Sample Requisition" }[mode];

  const headerActions = (
    <Space>
      <Button size="small" onClick={() => navigate(LIST_PATH)}>
        {isView ? "Back" : "Discard"}
      </Button>
      {isView ? (
        <Button
          size="small"
          type="primary"
          disabled={!hasPermission([SAMPLE_REQUISITION_EDIT_PERMISSION])}
          onClick={() => navigate(`${LIST_PATH}/edit/${id}`)}
        >
          Edit
        </Button>
      ) : (
        <Button size="small" type="primary" loading={saving} onClick={() => form.submit()}>
          Save
        </Button>
      )}
    </Space>
  );

  return (
    <AppContent
      header={<AppPageHeader title={<Title level={5}>{pageTitle}</Title>} extra={headerActions} />}
      content={
        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            requiredMark={!isView}
            disabled={isView}
            onFinish={onFinish}
            initialValues={{
              requisition_date: moment(),
              ...Object.fromEntries(BOOLEAN_FIELDS.map((field) => [field, false])),
              checklist: defaultChecklist(),
            }}
          >
            <Row gutter={16} align="stretch">
              <Col xs={24} xl={16} style={{ display: "flex", flexDirection: "column" }}>
                <Section stretch title="General Information">
                  <Row gutter={GUTTER}>
                    <Col {...HALF}>
                      <Form.Item label="Sample Reference">
                        <Input size="small" value={referenceNo ?? ""} placeholder="Loading..." readOnly disabled />
                      </Form.Item>
                    </Col>
                    <Col {...QUARTER}>
                      <Form.Item
                        label="Date"
                        name="requisition_date"
                        rules={[{ required: true, message: "Date is required" }]}
                      >
                        <CustomDatePicker />
                      </Form.Item>
                    </Col>
                    <Col {...QUARTER}>
                      <Form.Item label="Gmt Ex-Country Date" name="gmt_ex_country_date">
                        <CustomDatePicker />
                      </Form.Item>
                    </Col>
                    <Col {...QUARTER}>
                      <Form.Item label="Sample Requirement Date" name="sample_requirement_date">
                        <CustomDatePicker />
                      </Form.Item>
                    </Col>
                    <Col {...HALF}>
                      <Form.Item
                        label="Type of Sample"
                        name="sample_type_id"
                        rules={[{ required: true, message: "Type of sample is required" }]}
                      >
                        <Select
                          size="small"
                          showSearch
                          optionFilterProp="label"
                          placeholder="Select Type of Sample"
                          options={sampleTypes.map((type) => ({ label: type.name, value: type.id }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col {...QUARTER}>
                      <Form.Item label="Sample Qnty" name="sample_qty">
                        <InputNumber size="small" min={0} precision={0} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col {...QUARTER}>
                      <Form.Item label="Section" name="section">
                        <Input size="small" placeholder="CAD & SAMPLE" />
                      </Form.Item>
                    </Col>
                  </Row>
                </Section>
              </Col>

              <Col xs={24} xl={8} style={{ display: "flex", flexDirection: "column" }}>
                <Section stretch title="Attachments">
                  <Upload
                    action={`${API_BASE_URL}${MER_SAMPLE_REQUISITION_UPLOAD_IMAGE}`}
                    headers={{ authorization: `Bearer ${localStorage.getItem("token")}` }}
                    name="file"
                    accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.pdf,.xls,.xlsx,.doc,.docx"
                    listType="text"
                    multiple
                    fileList={imageList}
                    beforeUpload={beforeAttachmentUpload}
                    onChange={onImageChange}
                    onRemove={onImageRemove}
                    onPreview={(file) => file.url && window.open(file.url, "_blank")}
                    disabled={isView}
                  >
                    {!isView && (
                      <Button size="small" icon={<UploadOutlined />}>
                        Upload
                      </Button>
                    )}
                  </Upload>
                  <Text type="secondary">Image, PDF, Excel, or Word. Max 10MB each.</Text>
                  {isView && !imageList.length && (
                    <div>
                      <Text type="secondary">No files uploaded</Text>
                    </div>
                  )}
                </Section>
              </Col>
            </Row>

            <Section title="Buyer & Style">
              <Row gutter={GUTTER}>
                <Col {...FOURTH}>
                  <Form.Item label="Buyer" name="buyer_id" rules={[{ required: true, message: "Buyer is required" }]}>
                    <Select
                      size="small"
                      showSearch
                      optionFilterProp="label"
                      placeholder="Select Buyer"
                      onChange={onBuyerChange}
                      options={buyers.map((buyer) => ({ label: buyer.name, value: buyer.id }))}
                    />
                  </Form.Item>
                </Col>
                <Col {...FOURTH}>
                  <Form.Item label="Buying Office" name="buying_office_id">
                    <Select
                      size="small"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder={buyerId ? "Select Buying Office" : "Select a buyer first"}
                      disabled={isView || !buyerId}
                      onChange={onBuyingOfficeChange}
                      options={buyingOffices.map((office) => ({ label: office.name, value: office.id }))}
                    />
                  </Form.Item>
                </Col>
                <Col {...FOURTH}>
                  <Form.Item
                    label="Style"
                    name="style_ids"
                    rules={[{ required: true, message: "At least one style is required" }]}
                  >
                    <Select
                      size="small"
                      mode="multiple"
                      showSearch
                      optionFilterProp="label"
                      maxTagCount="responsive"
                      loading={stylesLoading}
                      placeholder={buyerId ? "Select Style(s)" : "Select a buyer first"}
                      disabled={isView || !buyerId}
                      onChange={onStylesChange}
                      options={styles.map((style) => ({ label: style.style_no, value: style.id }))}
                    />
                  </Form.Item>
                </Col>
                <Col {...FOURTH}>
                  <Form.Item label="Sub Style" name="sub_style_ids">
                    <Select
                      size="small"
                      mode="multiple"
                      showSearch
                      optionFilterProp="label"
                      maxTagCount="responsive"
                      allowClear
                      placeholder={styleIds.length ? "Select Sub Style(s)" : "Select a style first"}
                      disabled={isView || !styleIds.length}
                      options={subStyleOptions}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label="Description of Style" name="style_description">
                    <TextArea
                      size="small"
                      autoSize={{ minRows: 1, maxRows: 4 }}
                      readOnly
                      placeholder="Filled automatically from the selected style(s)"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Section>

            <Section title="Garment Details">
              <Row gutter={GUTTER}>
                <Col {...QUARTER}>
                  <Form.Item label="Model" name="model">
                    <Input size="small" />
                  </Form.Item>
                </Col>
                <Col {...QUARTER}>
                  <Form.Item label="Item" name="item">
                    <Input size="small" />
                  </Form.Item>
                </Col>
                <Col {...QUARTER}>
                  <Form.Item label="Color" name="color">
                    <Input size="small" />
                  </Form.Item>
                </Col>
                <Col {...QUARTER}>
                  <Form.Item label="Size" name="size">
                    <Input size="small" />
                  </Form.Item>
                </Col>
                <Col {...QUARTER}>
                  <Form.Item label="Pattern Ref" name="pattern_ref">
                    <Input size="small" />
                  </Form.Item>
                </Col>
                <Col {...QUARTER}>
                  <Form.Item label="Fabric Construction" name="fabric_construction">
                    <Input size="small" />
                  </Form.Item>
                </Col>
                <Col {...QUARTER}>
                  <Form.Item label="Pocketing" name="pocketing">
                    <Input size="small" />
                  </Form.Item>
                </Col>
                <Col {...QUARTER}>
                  <Form.Item label="Interlining" name="interlining">
                    <Input size="small" />
                  </Form.Item>
                </Col>
                <Col {...QUARTER}>
                  <Form.Item label="Thread's" name="threads">
                    <Input size="small" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label="Wash Detail" name="wash_detail">
                    <TextArea size="small" autoSize={{ minRows: 2, maxRows: 5 }} />
                  </Form.Item>
                </Col>
              </Row>
            </Section>

            <Section title="Trims & Embellishments">
              <Row gutter={GUTTER}>
                {YES_NO_FIELDS.map(({ label, name }) => (
                  <Col {...QUARTER} key={name}>
                    <Form.Item label={label} name={name}>
                      <YesNoRadio />
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            </Section>

            <Section title="Fabric Swatch & Instructions">
              <Row gutter={GUTTER}>
                <Col {...HALF}>
                  <Form.Item label="Body Fabric Swatch" name="body_fabric_swatch">
                    <TextArea size="small" rows={4} />
                  </Form.Item>
                </Col>
                <Col {...HALF}>
                  <Form.Item label="Contrast Fabric Swatch" name="contrast_fabric_swatch">
                    <TextArea size="small" rows={4} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label="Special Instruction (If Any)" name="special_instruction">
                    <TextArea size="small" autoSize={{ minRows: 2, maxRows: 6 }} />
                  </Form.Item>
                </Col>
              </Row>
            </Section>

            <Section title="Sample Department">
              <Row gutter={GUTTER}>
                <Col xs={24} md={8}>
                  <Form.Item label="Receive Date" name="receive_date">
                    <CustomDatePicker />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Expected Submission Date" name="expected_submission_date">
                    <CustomDatePicker />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Actual Submission Date" name="actual_submission_date">
                    <CustomDatePicker />
                  </Form.Item>
                </Col>
              </Row>
            </Section>

            <Section title="Items Check & Receive">
              <style>{`
                .sample-req-checklist .ant-table table { table-layout: fixed; }
                .sample-req-checklist .sample-req-sl {
                  width: 36px;
                  min-width: 36px;
                  max-width: 36px;
                  padding-left: 4px;
                  padding-right: 4px;
                }
                .sample-req-received-row {
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  height: 24px;
                }
                .sample-req-received-row .ant-picker-small {
                  height: 24px;
                }
                .sample-req-received.ant-checkbox-wrapper {
                  display: inline-flex;
                  align-items: center;
                  height: 24px;
                  margin: 0;
                  line-height: 1;
                }
                .sample-req-received .ant-checkbox {
                  top: 0;
                  display: inline-flex;
                  align-items: center;
                  line-height: 1;
                }
                .sample-req-received .ant-checkbox-inner {
                  width: 24px;
                  height: 24px;
                }
                .sample-req-received .ant-checkbox-checked .ant-checkbox-inner::after {
                  width: 7px;
                  height: 12px;
                  left: 30%;
                }
              `}</style>
              <Text type="secondary">Sample team updates received date and remarks for each item.</Text>
              <Form.List name="checklist">
                {(fields) => (
                  <Table
                    className="sample-req-checklist"
                    size="small"
                    bordered
                    pagination={false}
                    rowKey="key"
                    dataSource={fields}
                    style={{ marginTop: 12, marginBottom: 16 }}
                    columns={[
                      {
                        title: "SL",
                        width: 36,
                        align: "center",
                        onHeaderCell: () => ({ className: "sample-req-sl" }),
                        onCell: () => ({ className: "sample-req-sl" }),
                        render: (_, __, index) => index + 1,
                      },
                      {
                        title: "NEED ITEMS",
                        render: (_, field) => (
                          <>
                            <Form.Item name={[field.name, "item_name"]} hidden>
                              <Input />
                            </Form.Item>
                            {form.getFieldValue(["checklist", field.name, "item_name"])}
                          </>
                        ),
                      },
                      {
                        title: "RCVD & DATE",
                        width: 220,
                        render: (_, field) => (
                          <div className="sample-req-received-row">
                            <Form.Item name={[field.name, "is_received"]} valuePropName="checked" noStyle>
                              <Checkbox className="sample-req-received" />
                            </Form.Item>
                            <Form.Item name={[field.name, "received_date"]} noStyle>
                              <CustomDatePicker style={{ width: 160 }} />
                            </Form.Item>
                          </div>
                        ),
                      },
                      {
                        title: "REMARKS",
                        render: (_, field) => (
                          <Form.Item name={[field.name, "remarks"]} style={{ marginBottom: 0 }}>
                            <Input size="small" />
                          </Form.Item>
                        ),
                      },
                    ]}
                  />
                )}
              </Form.List>
            </Section>
          </Form>
        </Spin>
      }
    />
  );
}
